import { useState, useEffect } from "react";
import { Pencil, Plus, Trash2, X, Palette } from "lucide-react";
import { BASE_URL } from "../Api/Config";

const CategoryModal = ({ store, isOpen, onClose, onCategoriesUpdate, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (store) setCategories(store.categories || []);
  }, [store]);

  // const showToast = (title, description) => alert(`${title}\n${description}`);

  // Get auth token from localStorage or wherever you store it
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'your-auth-token';
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/categories/restaurant/${store.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': getAuthToken(),
        },
        body: JSON.stringify({
          name: newCategoryName.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const newCategory = result.data || result;
      const updatedCategories = [...categories, newCategory];
      
      setCategories(updatedCategories);
      setNewCategoryName("");
      setIsAdding(false);
      
      if (store) onCategoriesUpdate(store.id, updatedCategories);
      
      // Close the modal and refresh data
      onClose();
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 300);
      }
      
    } catch (error) {
      console.error('Error adding category:', error);
      // showToast("Error", "Failed to add category. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setIsAdding(true);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !newCategoryName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'authorization': getAuthToken(),
        },
        body: JSON.stringify({
          name: newCategoryName.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const updatedCategory = result.data || result; // Handle different response formats
      const updatedCategories = categories.map(cat =>
        cat.id === editingCategory.id ? updatedCategory : cat
      );
      
      setCategories(updatedCategories);
      setEditingCategory(null);
      setNewCategoryName("");
      setIsAdding(false);
      
      if (store) onCategoriesUpdate(store.id, updatedCategories);
      
      // Close the modal and refresh data
      onClose();
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 300);
      }
    } catch (error) {
      console.error('Error updating category:', error);
      // showToast("Error", "Failed to update category. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // const handleDeleteCategory = async (categoryId) => {
  //   const categoryToDelete = categories.find(c => c.id === categoryId);
    
  //   setIsLoading(true);
  //   try {
  //     const response = await fetch(`${BASE_URL}/categories/${categoryId}`, {
  //       method: 'DELETE',
  //       headers: {
  //         'authorization': getAuthToken(),
  //       }
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const updatedCategories = categories.filter(c => c.id !== categoryId);
  //     setCategories(updatedCategories);
      
  //     if (store) onCategoriesUpdate(store.id, updatedCategories);
      
  //     // Close modal and refresh data  
  //     onClose();
  //     if (onRefresh) {
  //       setTimeout(() => {
  //         onRefresh();
  //       }, 300);
  //     }
  //   } catch (error) {
  //     console.error('Error deleting category:', error);
  //     // showToast("Error", "Failed to delete category. Please try again.");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleCancel = () => {
    setEditingCategory(null);
    setNewCategoryName("");
    setIsAdding(false);
  };

  if (!isOpen || !store) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-lg p-6">
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-xl z-20">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Processing...</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Palette className="w-5 h-5 text-blue-600" />
            Categories for {store.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add/Edit Form */}
        {isAdding && (
          <div className="mb-4 border rounded p-4 bg-gray-50">
            <label className="block mb-2 text-sm font-medium">
              {editingCategory ? "Edit Category" : "Add New Category"}
            </label>
            <div className="flex gap-2">
              <input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name..."
                className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isLoading) {
                    editingCategory ? handleUpdateCategory() : handleAddCategory();
                  }
                  if (e.key === "Escape" && !isLoading) handleCancel();
                }}
                disabled={isLoading}
                autoFocus
              />
              <button
                onClick={editingCategory ? handleUpdateCategory : handleAddCategory}
                disabled={!newCategoryName.trim() || isLoading}
                className="bg-blue-600 text-white px-3 py-2 rounded disabled:opacity-50"
              >
                {editingCategory ? "Update" : "Add"}
              </button>
              <button
                onClick={handleCancel}
                className="border px-3 py-2 rounded"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Add Button */}
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading}
          >
            <Plus className="w-4 h-4" />
            Add New Category
          </button>
        )}

        {/* Categories List */}
        {categories.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                🎨
              </div>
            </div>
            <p>No categories yet. Add your first category!</p>
          </div>
        ) : (
          <ul className="space-y-2 mt-4">
            {categories.map((category) => (
              <li key={category.id || category.name} className="flex justify-between items-center p-3 border rounded hover:bg-gray-50">
                <span className="font-medium text-gray-900">
                  {category.name}
                </span>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleEditCategory(category)} 
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {/* <button 
                    onClick={() => handleDeleteCategory(category.id)} 
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button> */}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CategoryModal;